import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { ScrollToTop } from './components/ScrollToTop';
import * as authApi from './api/auth';

const Home = React.lazy(() => import('./pages/Home'));
const About = React.lazy(() => import('./pages/About'));
const Platform = React.lazy(() => import('./pages/Platform'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const Integrations = React.lazy(() => import('./pages/Integrations'));
const SolutionsHub = React.lazy(() => import('./pages/SolutionsHub'));
const IndustrySolution = React.lazy(() => import('./pages/IndustrySolution'));
const SizeSolution = React.lazy(() => import('./pages/SizeSolution'));
const ResourcesHub = React.lazy(() => import('./pages/ResourcesHub'));
const Blog = React.lazy(() => import('./pages/Blog'));
const BlogArticle = React.lazy(() => import('./pages/BlogArticle'));
const Guides = React.lazy(() => import('./pages/Guides'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions = React.lazy(() => import('./pages/TermsConditions'));
const Security = React.lazy(() => import('./pages/Security'));
const DevelopersAgentTrial = React.lazy(() => import('./pages/DevelopersAgentTrial'));
const Auth = React.lazy(() => import('./pages/Auth'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const AnalyticsDashboard = React.lazy(() => import('./pages/AnalyticsDashboard'));
const Profile = React.lazy(() => import('./pages/Profile'));
const OauthGoogle = React.lazy(() => import('./pages/OauthGoogle'));

function shouldLoadTrackers(): boolean {
  // Avoid loading Firebase-backed trackers on the initial critical path.
  // They can be enabled later, or kept off in dev/build environments.
  return import.meta.env.PROD && import.meta.env.VITE_ENABLE_FIREBASE === 'true';
}

// Placeholder components for other routes

export default function App() {
  const [trackersEnabled, setTrackersEnabled] = useState(false);

  const pageFallback = (
    <div className="min-h-[70vh] w-full">
      <div className="container mx-auto px-4 md:px-6 pt-10">
        <div className="h-7 w-2/3 max-w-xl rounded bg-gray-100" />
        <div className="mt-4 h-5 w-1/2 max-w-lg rounded bg-gray-100" />
        <div className="mt-10 h-64 w-full rounded-2xl bg-gray-50 border border-gray-100" />
      </div>
    </div>
  );

  useEffect(() => {
    // Belt-and-suspenders: drop bot prerender HTML once React is alive
    document.getElementById('static-content')?.remove();
  }, []);

  useEffect(() => {
    if (!shouldLoadTrackers()) return;

    const enable = () => setTrackersEnabled(true);

    // Prefer requestIdleCallback to keep main thread free for first paint.
    const w = window as any;
    if (typeof w.requestIdleCallback === 'function') {
      const id = w.requestIdleCallback(enable, { timeout: 1500 });
      return () => w.cancelIdleCallback?.(id);
    }

    const t = window.setTimeout(enable, 800);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <EmailConfirmLinkHandler />
          {trackersEnabled ? (
            <Suspense fallback={null}>
              <DeferredTrackers />
            </Suspense>
          ) : null}
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route
                index
                element={
                  <Home />
                }
              />
              <Route
                path="about"
                element={
                  <Suspense fallback={pageFallback}>
                    <About />
                  </Suspense>
                }
              />
              <Route
                path="platform"
                element={
                  <Suspense fallback={pageFallback}>
                    <Platform />
                  </Suspense>
                }
              />
              <Route
                path="platform/:id"
                element={
                  <Suspense fallback={pageFallback}>
                    <Platform />
                  </Suspense>
                }
              />{' '}
              {/* Simplified for now */}
              <Route
                path="solutions"
                element={
                  <Suspense fallback={pageFallback}>
                    <SolutionsHub />
                  </Suspense>
                }
              />
              <Route
                path="solutions/industry/:id"
                element={
                  <Suspense fallback={pageFallback}>
                    <IndustrySolution />
                  </Suspense>
                }
              />
              <Route
                path="solutions/size/:id"
                element={
                  <Suspense fallback={pageFallback}>
                    <SizeSolution />
                  </Suspense>
                }
              />
              <Route
                path="integrations"
                element={
                  <Suspense fallback={pageFallback}>
                    <Integrations />
                  </Suspense>
                }
              />
              <Route path="pricing" element={<Pricing />} />
              <Route
                path="resources"
                element={
                  <Suspense fallback={pageFallback}>
                    <ResourcesHub />
                  </Suspense>
                }
              />
              <Route
                path="resources/blog"
                element={
                  <Suspense fallback={pageFallback}>
                    <Blog />
                  </Suspense>
                }
              />
              <Route
                path="resources/blog/:slug"
                element={
                  <Suspense fallback={pageFallback}>
                    <BlogArticle />
                  </Suspense>
                }
              />
              <Route
                path="resources/guides"
                element={
                  <Suspense fallback={pageFallback}>
                    <Guides />
                  </Suspense>
                }
              />
              <Route
                path="privacy"
                element={
                  <Suspense fallback={pageFallback}>
                    <PrivacyPolicy />
                  </Suspense>
                }
              />
              <Route
                path="terms"
                element={
                  <Suspense fallback={pageFallback}>
                    <TermsConditions />
                  </Suspense>
                }
              />
              <Route
                path="security"
                element={
                  <Suspense fallback={pageFallback}>
                    <Security />
                  </Suspense>
                }
              />
              <Route
                path="developers/agent-trial"
                element={
                  <Suspense fallback={pageFallback}>
                    <DevelopersAgentTrial />
                  </Suspense>
                }
              />
              <Route
                path="contact"
                element={
                  <Suspense fallback={pageFallback}>
                    <Contact />
                  </Suspense>
                }
              />
              <Route
                path="auth"
                element={
                  <Suspense fallback={pageFallback}>
                    <Auth />
                  </Suspense>
                }
              />
              <Route
                path="oauth/google"
                element={
                  <Suspense fallback={pageFallback}>
                    <OauthGoogle />
                  </Suspense>
                }
              />
              <Route
                path="admin"
                element={
                  <Suspense fallback={pageFallback}>
                    <AdminDashboard />
                  </Suspense>
                }
              />
              <Route
                path="admin/analytics"
                element={
                  <Suspense fallback={pageFallback}>
                    <AnalyticsDashboard />
                  </Suspense>
                }
              />
              <Route
                path="profile"
                element={
                  <Suspense fallback={pageFallback}>
                    <Profile />
                  </Suspense>
                }
              />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

function EmailConfirmLinkHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const qp = new URLSearchParams(location.search);
    const confirmSlug = qp.get('email-link-refer');
    const forgotSlug = qp.get('forgot-password-refer');

    if (forgotSlug) {
      navigate(`/auth?mode=reset-password&slug=${encodeURIComponent(forgotSlug)}`, { replace: true });
      return;
    }

    if (!confirmSlug) return;

    let cancelled = false;
    (async () => {
      try {
        await authApi.verifyEmail(confirmSlug);
        if (cancelled) return;
        navigate('/auth?mode=login&emailConfirmed=1', { replace: true });
      } catch {
        if (cancelled) return;
        navigate('/auth?mode=login&emailConfirmed=0', { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.search, navigate]);

  return null;
}

const DeferredTrackers = React.lazy(async () => {
  const mod = await import('./components/DeferredTrackers.tsx');
  return { default: mod.DeferredTrackers };
});
